import { ArrowsClockwise, CreditCard, Wallet } from '@phosphor-icons/react'
import { type Language } from './LandingPage'

const content = {
  pt: {
    eyebrow: 'Modelos de cobrança',
    title: 'One-off e recorrente, cada um no fluxo certo.',
    subtitle: 'A home agora deixa explícito o que é cobrança única com sinal e o que é assinatura recorrente via Stripe.',
    oneOff: {
      title: 'One-off',
      description: 'Cobrança única para confirmar a reserva com sinal e, quando fizer sentido, saldo posterior.',
      bullets: [
        'Ideal para eventos, sessões, diárias e reservas configuradas',
        'Permite sinal/depósito e saldo posterior',
        'Funciona em todos os planos',
      ],
    },
    recurring: {
      title: 'Recorrente',
      description: 'Assinatura via checkout Stripe para serviços contínuos e contratos mensais.',
      bullets: [
        'Ideal para serviços mensais ou contínuos',
        'Disponível em Pro e Studio',
        'Sem sinal e sem add-ons no fluxo atual',
      ],
    },
  },
  en: {
    eyebrow: 'Payment modes',
    title: 'One-off and recurring, each in the right flow.',
    subtitle: 'The landing now makes it explicit what is a single charge with deposit and what is a recurring Stripe subscription.',
    oneOff: {
      title: 'One-off',
      description: 'A single charge to confirm the booking with a deposit and, when needed, a remaining balance later.',
      bullets: [
        'Best for events, sessions, day rates, and configured bookings',
        'Supports deposit and later balance',
        'Available on every plan',
      ],
    },
    recurring: {
      title: 'Recurring',
      description: 'Stripe subscription checkout for ongoing services and monthly retainers.',
      bullets: [
        'Best for ongoing or monthly services',
        'Available on Pro and Studio',
        'No deposit and no add-ons in the current flow',
      ],
    },
  },
  es: {
    eyebrow: 'Tipos de cobro',
    title: 'One-off y recurrente, cada uno en el flujo correcto.',
    subtitle: 'La landing ahora deja claro qué es un cobro único con seña y qué es una suscripción recurrente vía Stripe.',
    oneOff: {
      title: 'One-off',
      description: 'Cobro único para confirmar la reserva con seña y, cuando haga sentido, saldo posterior.',
      bullets: [
        'Ideal para eventos, sesiones, jornadas y reservas configuradas',
        'Permite seña y saldo posterior',
        'Disponible en todos los planes',
      ],
    },
    recurring: {
      title: 'Recurrente',
      description: 'Checkout de suscripción con Stripe para servicios continuos y contratos mensuales.',
      bullets: [
        'Ideal para servicios mensuales o continuos',
        'Disponible en Pro y Studio',
        'Sin seña y sin add-ons en el flujo actual',
      ],
    },
  },
}

interface PaymentModesProps {
  language: Language
}

export function PaymentModes({ language }: PaymentModesProps) {
  const t = content[language]

  return (
    <section id="payment-modes" className="px-6 py-20">
      <div className="mx-auto max-w-7xl">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#f08a24]">{t.eyebrow}</p>
          <h2 className="mt-4 text-4xl font-semibold tracking-tight text-[#0b2a4a] sm:text-5xl">{t.title}</h2>
          <p className="mt-4 text-lg text-[#0b2a4a]/72">{t.subtitle}</p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <article className="rounded-[32px] border border-[#0b2a4a]/10 bg-white p-8 shadow-[0_30px_80px_rgba(11,42,74,0.08)]">
            <div className="flex items-center gap-3 text-[#0b2a4a]">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#f08a24]/12">
                <Wallet size={24} />
              </div>
              <h3 className="text-2xl font-semibold">{t.oneOff.title}</h3>
            </div>
            <p className="mt-5 text-base leading-7 text-[#0b2a4a]/72">{t.oneOff.description}</p>
            <ul className="mt-6 space-y-3 text-sm text-[#0b2a4a]/82">
              {t.oneOff.bullets.map((bullet) => (
                <li key={bullet} className="flex items-start gap-3">
                  <CreditCard size={18} className="mt-0.5 text-[#f08a24]" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </article>

          <article className="rounded-[32px] border border-[#0b2a4a]/10 bg-[#0b2a4a] p-8 text-[#f6ebdd] shadow-[0_30px_80px_rgba(11,42,74,0.16)]">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
                <ArrowsClockwise size={24} />
              </div>
              <h3 className="text-2xl font-semibold">{t.recurring.title}</h3>
            </div>
            <p className="mt-5 text-base leading-7 text-[#f6ebdd]/76">{t.recurring.description}</p>
            <ul className="mt-6 space-y-3 text-sm text-[#f6ebdd]/86">
              {t.recurring.bullets.map((bullet) => (
                <li key={bullet} className="flex items-start gap-3">
                  <CreditCard size={18} className="mt-0.5 text-[#f08a24]" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </article>
        </div>
      </div>
    </section>
  )
}
